import { useCallback, useEffect, useMemo, useState } from 'react';
import { CONFIGURATION_PRODUCT_OPTION_ID, CYLINDO_OPTIONS, FABRIC_PRODUCT_OPTION_ID, LEATHER_PRODUCT_OPTION_ID, PIECE_PRODUCT_OPTION_ID } from 'commons/constants';
import { orientationOptionIds } from 'components/consumer/OrientationPDPModal';
import usePDPOptions from 'components/consumer/PDP/state/context/usePDPOptions';
import usePDPBasicDetails from 'components/consumer/PDP/state/context/usePDPBasicDetails';
import useCylindoDimensionShots from '../useCylindoDimensionShots';
import useCylindoCache from './cylindoCache/useCylindoCache';
import resolveCylindoUpholsteryCode from './resolveCylindoUpholsteryCode';
import resolveCylindoPowerOptionsCode from './resolveCylindoPowerOptionsCode';
import resolveCylindoOrientationCode from './resolveCylindoOrientationCode';
import resolveCylindoOpenCloseOption from './resolveCylindoOpenCloseOption';
import buildCylindoUrl from './cylindoCache/buildCylindoUrl';
import { DISABLED_UPHOLSTERY, EMPTY_ARRAY, EMPTY_OBJECT, CYLINDO_FRAMES_TO_SHOW } from './cylindoCache/defaults';

// CON-7441: Open/Close is a view mode (not a purchase option), so it toggles the live
// viewer via cylindoFeatures. Power (2/3 recliner) is a backend option (170), driven
// from the "Select Configuration" dropdown, not here.
//
// Cylindo authors this feature under several names, and we can only match by name (see
// CYLINDO_OPTIONS in commons/constants for why). A new name needs a line added here
// (CON-8732 found LEG POSITION and SLEEPER OPTIONS).
const MECHANICAL_OPEN = 'OPEN';
const MECHANICAL_CLOSED = 'CLOSE';
const MECHANICAL_FEATURE_CODES = [CYLINDO_OPTIONS.MECHANICAL, CYLINDO_OPTIONS.RECLINING_OPTIONS, CYLINDO_OPTIONS.LEG_POSITION, CYLINDO_OPTIONS.SLEEPER_OPTIONS];
const usePDPCylindoViewer = ({
  isPDPDesktop = false,
  material = null,
  deferCylindoFetch = false
} = {}) => {
  const {
    data: basicDetails
  } = usePDPBasicDetails();
  const {
    data: {
      selectedOptionsExtended
    }
  } = usePDPOptions();
  const productId = basicDetails?.id;
  const isCylindoProduct = !!basicDetails?.custom_properties?.cylindo_product;

  // Open/Close view-mode state. Default CLOSED so recliners land closed on page load
  // (CON-7441). Reset on product change (this hook persists across SPA PDP→PDP nav).
  const [openCloseState, setOpenCloseState] = useState(MECHANICAL_CLOSED);
  useEffect(() => {
    setOpenCloseState(MECHANICAL_CLOSED);
  }, [productId]);
  const {
    config: cylindoProductConfig,
    version: cylindoVersion,
    loading
  } = useCylindoCache(productId, isCylindoProduct, deferCylindoFetch);
  const upholsteryKey = useMemo(() => {
    if (!productId || !cylindoProductConfig || !selectedOptionsExtended) {
      return null;
    }
    const fabricOption = selectedOptionsExtended[FABRIC_PRODUCT_OPTION_ID]?.value;
    const leatherOption = selectedOptionsExtended[LEATHER_PRODUCT_OPTION_ID]?.value;
    const selectedOption = fabricOption || leatherOption || null;
    const jbName = material?.label ?? selectedOption?.value ?? null;
    const jbSku = selectedOption?.sku ?? null;
    if (!jbName) {
      return null;
    }
    const upholsteryFeature = cylindoProductConfig.features?.find(feat => feat.code === CYLINDO_OPTIONS.UPHOLSTERY);
    const resolved = resolveCylindoUpholsteryCode({
      jbSku,
      jbName,
      cylindoOptions: upholsteryFeature?.options
    });
    if (resolved && DISABLED_UPHOLSTERY.includes(resolved)) {
      return null;
    }
    return resolved;
  }, [cylindoProductConfig, material?.label, productId, selectedOptionsExtended]);
  const hasUpholsteryOption = !!upholsteryKey;
  const dimensionShotsData = useCylindoDimensionShots({
    upholsteryKey,
    cylindoVersion
  });
  const upholsteryFeatureDetails = useMemo(() => {
    if (!upholsteryKey) {
      return undefined;
    }
    return {
      [CYLINDO_OPTIONS.UPHOLSTERY]: upholsteryKey
    };
  }, [upholsteryKey]);
  const isUpholsteryFeatureAvailable = !!upholsteryFeatureDetails;
  const orientationKey = useMemo(() => {
    const orientationId = orientationOptionIds.find(id => selectedOptionsExtended[id]);
    const orientationValue = selectedOptionsExtended[orientationId]?.value?.value;
    return orientationValue?.toUpperCase();
  }, [selectedOptionsExtended]);
  const hasOrientationOption = !!orientationKey;
  const orientationOptionDetails = useMemo(() => {
    if (!orientationKey) {
      return undefined;
    }
    const orientationFeature = cylindoProductConfig?.features?.find(feat_0 => feat_0.code === CYLINDO_OPTIONS.ORIENTATION);
    const orientationCode = resolveCylindoOrientationCode({
      jbValue: orientationKey,
      cylindoOptions: orientationFeature?.options
    });
    if (!orientationCode) {
      return undefined;
    }
    return {
      [CYLINDO_OPTIONS.ORIENTATION]: orientationCode
    };
  }, [cylindoProductConfig?.features, orientationKey]);
  const isOrientationFeatureAvailable = !!orientationOptionDetails;

  // Power (2/3 recliner): read the user's "Select Configuration" choice (option 170) and
  // resolve it to the live POWER OPTIONS code. The backend option gates which counts exist.
  const powerOptionDetails = useMemo(() => {
    const selected = selectedOptionsExtended?.[CONFIGURATION_PRODUCT_OPTION_ID]?.value;
    if (!selected) {
      return undefined;
    }
    const powerFeature = cylindoProductConfig?.features?.find(feat_1 => feat_1.code === CYLINDO_OPTIONS.POWER_OPTIONS);
    const code = resolveCylindoPowerOptionsCode({
      jbSku: selected.sku,
      jbName: selected.value,
      cylindoOptions: powerFeature?.options
    });
    return code ? {
      [CYLINDO_OPTIONS.POWER_OPTIONS]: code
    } : undefined;
  }, [cylindoProductConfig?.features, selectedOptionsExtended]);

  // Non Reclining (backend option 171 "Piece") has no render of its own yet, so it borrows
  // the reclining model closed. Keyed on the sku, not the value text, which varies.
  const isNonReclining = useMemo(() => {
    const sku = selectedOptionsExtended?.[PIECE_PRODUCT_OPTION_ID]?.value?.sku ?? '';
    return /NNRCLN/i.test(sku);
  }, [selectedOptionsExtended]);

  // Force closed for Non Reclining, but leave openCloseState alone so the user's pick
  // returns if they switch back to Reclining.
  const effectiveOpenCloseState = isNonReclining ? MECHANICAL_CLOSED : openCloseState;

  // Newer configs name a render per recliner count, so read the count out of the
  // selected configuration or orientation (CON-8732).
  const selectedReclinerCount = useMemo(() => {
    const candidates = [selectedOptionsExtended?.[CONFIGURATION_PRODUCT_OPTION_ID]?.value?.value, ...orientationOptionIds.map(id_0 => selectedOptionsExtended?.[id_0]?.value?.value)];
    return candidates.map(value => /(\d+)\s*reclin/i.exec(value || '')?.[1]).find(Boolean) ?? null;
  }, [selectedOptionsExtended]);

  // Open/Close resolved against the live config. Feature name varies by product family
  // (MECHANICAL_FEATURE_CODES), so match any of them and key the URL off the feature's
  // own code. Resolve both poses up front: the toggle only shows when both render for
  // the selected count, so the user cannot open into a pose that has no render and lose
  // the toggle (CON-8732).
  const openCloseCodes = useMemo(() => {
    const mechanicalFeature = cylindoProductConfig?.features?.find(feat_2 => MECHANICAL_FEATURE_CODES.includes(feat_2.code));
    const resolve = isOpen => resolveCylindoOpenCloseOption({
      isOpen,
      selectedReclinerCount,
      cylindoOptions: mechanicalFeature?.options
    });
    return {
      featureCode: mechanicalFeature?.code,
      open: resolve(true),
      closed: resolve(false)
    };
  }, [cylindoProductConfig?.features, selectedReclinerCount]);
  const hasOpenCloseFeature = !!(openCloseCodes.open && openCloseCodes.closed);

  // Without both poses, pin closed so the image never falls back to Cylindo's default.
  const openCloseDetails = useMemo(() => {
    const {
      featureCode,
      open,
      closed
    } = openCloseCodes;
    const code_0 = hasOpenCloseFeature && effectiveOpenCloseState === MECHANICAL_OPEN ? open : closed;
    return code_0 ? {
      [featureCode]: code_0
    } : undefined;
  }, [openCloseCodes, hasOpenCloseFeature, effectiveOpenCloseState]);
  const areAllFeaturesAvailable = (!hasUpholsteryOption || hasUpholsteryOption && isUpholsteryFeatureAvailable) && (!hasOrientationOption || hasOrientationOption && isOrientationFeatureAvailable);
  const cylindoFeatures = useMemo(() => ({
    ...(upholsteryFeatureDetails || EMPTY_OBJECT),
    ...(orientationOptionDetails || EMPTY_OBJECT),
    ...(powerOptionDetails || EMPTY_OBJECT),
    ...(openCloseDetails || EMPTY_OBJECT)
  }), [orientationOptionDetails, upholsteryFeatureDetails, powerOptionDetails, openCloseDetails]);
  const cylindoConfigurationPhotos = useMemo(() => {
    if (isPDPDesktop && areAllFeaturesAvailable && Object.entries(cylindoFeatures).length) {
      // Cylindo can fail to render a fabric even when its config lists it (broken
      // material link, CON-8451). The live viewer falls back by dropping the failing
      // feature; give the thumbnails the same fallback URL so they degrade identically
      // instead of showing broken images.
      const {
        [CYLINDO_OPTIONS.UPHOLSTERY]: upholstery,
        ...featuresWithoutUpholstery
      } = cylindoFeatures;
      return CYLINDO_FRAMES_TO_SHOW.map((frame, index) => ({
        id: frame,
        index,
        src: buildCylindoUrl({
          productId,
          kind: 'frames',
          frame,
          // these only feed the 44x38 thumbnail img, so cap the render
          // instead of pulling Cylindo's full-res default frame
          size: 128,
          features: cylindoFeatures
        }),
        fallbackSrc: upholstery ? buildCylindoUrl({
          productId,
          kind: 'frames',
          frame,
          size: 128,
          features: featuresWithoutUpholstery
        }) : undefined,
        type: 'cylindo'
      }));
    }
    return EMPTY_ARRAY;
  }, [areAllFeaturesAvailable, cylindoFeatures, isPDPDesktop, productId]);

  // null hides the strip toggle: no mechanical feature, or Non Reclining. Else the open state.
  const isMechanicalOpen = isPDPDesktop && hasOpenCloseFeature && !isNonReclining ? openCloseState === MECHANICAL_OPEN : null;
  const toggleMechanicalOpen = useCallback(() => {
    setOpenCloseState(prev => prev === MECHANICAL_OPEN ? MECHANICAL_CLOSED : MECHANICAL_OPEN);
  }, []);
  return {
    cylindoConfigurationPhotos,
    cylindoFeatures,
    isMechanicalOpen,
    toggleMechanicalOpen,
    isCylindoAvailable: !!cylindoProductConfig && areAllFeaturesAvailable,
    loading,
    isCylindoConfigurationPhotos: isCylindoProduct && !!cylindoProductConfig && areAllFeaturesAvailable && cylindoConfigurationPhotos?.length > 0,
    upholsteryKey,
    ...dimensionShotsData
  };
};
export default usePDPCylindoViewer;